import React, { useCallback, useMemo } from 'react'

import { KEY_VALUES } from '../../constants'
import { useTranslation } from '../../contexts/I18nContext'
import { sequentialArray, formatIngredientAmount } from '../../helpers'
import useMeasureSystem from '../../hooks/useMeasureSystem'
import { COLORS } from '../../styles'
import Ingredient from '../Ingredient'

import Styled from './RecipeConstituents.styled'

const RecipeConstituents = ({
  additionalConstituents,
  className,
  constituents,
  userConstituents,
  coversCount,
  isLoading,
  showError,
  withIngredientLinks,
}) => {
  const { t } = useTranslation()
  const handleAdditionalIngredientsClick = useCallback(() => {
    showError({ code: 'ADDITIONAL_INGREDIENTS' })
  }, [showError])

  const { measureSystem } = useMeasureSystem()

  const filteredAdditionalConstituents = useMemo(() => {
    return (
      additionalConstituents?.filter(
        ({ isHidden, ingredient } = {}) => !isHidden && !!ingredient,
      ) || []
    )
  }, [additionalConstituents])

  return (
    <Styled className={className}>
      <Styled.ingredientList>
        <Styled.ingredientListInner>
          {isLoading ? (
            sequentialArray(3).map((i) => (
              <Styled.ingredient key={i}>
                <Ingredient isLoading />
              </Styled.ingredient>
            ))
          ) : (
            <>
              {(constituents || []).map((constituent) => {
                return withIngredientLinks ? (
                  <Styled.ingredientLink
                    key={constituent.id || constituent.name}
                    to={`/ingredients/${constituent.id}`}
                    prefetch={false}
                    onClick={(event) => event.preventDefault()}
                    tabIndex={-1}
                  >
                    <Ingredient
                      name={constituent.name}
                      isOptional={constituent.isOptional}
                      imageUrl={constituent.imageUrl}
                      coversCount={coversCount}
                      quantityPerCover={constituent.quantityPerCover}
                      additionalMeasures={constituent.additionalMeasures}
                      unit={constituent.unit}
                    />
                  </Styled.ingredientLink>
                ) : (
                  <Styled.ingredient key={constituent.id || constituent.name}>
                    <Ingredient
                      name={constituent.name}
                      isOptional={constituent.isOptional}
                      imageUrl={constituent.imageUrl}
                      coversCount={coversCount}
                      quantityPerCover={constituent.quantityPerCover}
                      additionalMeasures={constituent.additionalMeasures}
                      unit={constituent.unit}
                    />
                  </Styled.ingredient>
                )
              })}
              {(userConstituents || []).map((userConstituent) => (
                <Styled.ingredient
                  key={userConstituent.id || userConstituent.name}
                >
                  <Ingredient
                    name={userConstituent.name}
                    isOptional={userConstituent.isOptional}
                    imageUrl={userConstituent.imageUrl}
                    coversCount={coversCount}
                    quantityPerCover={userConstituent.quantityPerCover}
                    additionalMeasures={userConstituent.additionalMeasures}
                    unit={userConstituent.unit}
                  />
                </Styled.ingredient>
              ))}
            </>
          )}
        </Styled.ingredientListInner>
      </Styled.ingredientList>
      {filteredAdditionalConstituents.length > 0 ? (
        <Styled.additionalIngredientsList>
          <Styled.additionalIngredientsLabel>
            {t('RECIPE.ADDITIONAL_INGREDIENTS')}
          </Styled.additionalIngredientsLabel>
          <Styled.additionalIngredients
            role="button"
            tabIndex={0}
            onClick={handleAdditionalIngredientsClick}
            onKeyPress={(event) => {
              if (event.key === KEY_VALUES.ENTER) {
                handleAdditionalIngredientsClick()
              }
            }}
          >
            {filteredAdditionalConstituents.map(
              (additionalConstituent, index) => (
                <Styled.additionalIngredient
                  key={additionalConstituent.ingredient.id}
                >
                  <Styled.additionalIngredientName>
                    {additionalConstituent.ingredient.name}
                  </Styled.additionalIngredientName>
                  &nbsp;
                  <Styled.additionalIngredientQuantity>
                    {`(${formatIngredientAmount({
                      amountsAndUnits: [
                        {
                          unit: additionalConstituent.unit,
                          amount:
                            coversCount *
                            additionalConstituent.quantityPerCover,
                        },
                        ...(additionalConstituent.additionalMeasures || []).map(
                          (am) => ({
                            unit: am.unit,
                            amount: coversCount * am.amount,
                          }),
                        ),
                      ],
                      measureSystem,
                    })})`}
                  </Styled.additionalIngredientQuantity>
                  {index === additionalConstituents.length - 1 ? null : (
                    <>
                      {','}
                      &nbsp;
                    </>
                  )}
                </Styled.additionalIngredient>
              ),
            )}
            <Styled.additionalIngredientsHelp
              name="information-fill-16"
              fill={COLORS.cobalt}
              width={16}
            />
          </Styled.additionalIngredients>
        </Styled.additionalIngredientsList>
      ) : null}
    </Styled>
  )
}

RecipeConstituents.defaultProps = {
  additionalConstituents: [],
  constituents: [],
  userConstituents: [],
  coversCount: 2,
  isLoading: false,
  withIngredientLinks: true,
}

export default RecipeConstituents
